import {
  generateSftpGroupPath,
  getPhoneNumberError,
  getSftpFieldsError,
  normalizePhoneNumber,
  SFTP_NOT_FOR_INTERNAL_MESSAGE,
  STEERFI_PARTY_ID,
} from '@steerfi/shared';
import {
  AlertCircle,
  Building2,
  Calendar,
  Check,
  Clock,
  Loader2,
  Mail,
  Pencil,
  Phone,
  Shield,
  User,
  X,
} from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { type ClientData, getClients } from '@/api/clients';
import {
  getUserById,
  type KeycloakUser,
  type RealmRole,
  type UpdateUserRequest,
  type UserDetail,
  updateUser,
} from '@/api/users';
import {
  ACCESS_LEVEL_LABEL,
  AccessLevel,
  accessLevelFromScopeTier,
  resolveAccessLevel,
  rolesForAccessLevel,
} from '@/lib/authz';
import { formatDate, formatDateTime } from '@/lib/utils';
import { formatErrorMessage } from '@/utils/error-formatting';
import { SftpAccessFields } from './sftp-access-fields';
import { SshPublicKeySummary } from './ssh-public-keys-editor';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Checkbox } from './ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  focusDialogOnOpen,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

interface UserDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  user: KeycloakUser | null;
  onUserUpdated?: () => void;
}

interface InfoCardProps {
  icon: React.ReactNode;
  title: string;
  value: string;
}

const InfoCard: React.FC<InfoCardProps> = ({ icon, title, value }) => (
  <Card>
    <CardContent className="p-3 text-center">
      <div className="flex items-center justify-center gap-2 mb-1.5 text-primary">
        {icon}
        <span className="text-xs text-muted-foreground">{title}</span>
      </div>
      <div className="font-medium text-sm">{value}</div>
    </CardContent>
  </Card>
);

interface EditFormState {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  partyId: string;
  roles: RealmRole[];
  enabled: boolean;
  isSftp: boolean;
  sftpUsername: string;
  keycloakGroup: string;
  sshPublicKeys: string[];
}

function FormRow({
  label,
  htmlFor,
  required,
  alignTop,
  children,
}: {
  label: string;
  htmlFor?: string;
  required?: boolean;
  alignTop?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={`grid grid-cols-4 gap-4 ${alignTop ? 'items-start' : 'items-center'}`}>
      <Label htmlFor={htmlFor} className={`text-right text-sm ${alignTop ? 'pt-2' : ''}`}>
        {label}
        {required && ' *'}
      </Label>
      <div className="col-span-3">{children}</div>
    </div>
  );
}

export const UserDetailsDialog: React.FC<UserDetailsDialogProps> = ({
  open,
  onClose,
  user,
  onUserUpdated,
}) => {
  const [detailedData, setDetailedData] = useState<UserDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [clients, setClients] = useState<ClientData[]>([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [editForm, setEditForm] = useState<EditFormState>({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    partyId: '',
    roles: ['basic'],
    enabled: true,
    isSftp: false,
    sftpUsername: '',
    keycloakGroup: '',
    sshPublicKeys: [],
  });

  const loadClients = useCallback(async () => {
    try {
      setClientsLoading(true);
      const response = await getClients({ limit: 100, sortBy: 'name', order: 'asc' });
      setClients(response.data);
    } catch {
      // user will see empty dropdown
    } finally {
      setClientsLoading(false);
    }
  }, []);

  const fetchUserDetails = useCallback(async (userId: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await getUserById(userId);
      setDetailedData(data);
    } catch (err) {
      console.error('Failed to fetch user details:', err);
      setError('Failed to load user details');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && user?.id) {
      fetchUserDetails(user.id);
    } else {
      setDetailedData(null);
      setIsEditing(false);
    }
  }, [open, user?.id, fetchUserDetails]);

  const handleClose = () => {
    setDetailedData(null);
    setError(null);
    setIsEditing(false);
    setSaveError(null);
    onClose();
  };

  const enterEditMode = () => {
    const data = detailedData || user;
    if (!data) return;
    loadClients();
    setEditForm({
      firstName: data.firstName || '',
      lastName: data.lastName || '',
      email: data.email || '',
      phoneNumber: detailedData?.phoneNumber || '',
      partyId: data.partyId || '',
      roles: data.roles?.filter((r): r is RealmRole =>
        ['internal', 'admin', 'basic'].includes(r)
      ) || ['basic'],
      enabled: data.enabled,
      isSftp: detailedData?.isSftp || false,
      sftpUsername: detailedData?.sftpUsername || '',
      keycloakGroup: detailedData?.keycloakGroup || '',
      sshPublicKeys: detailedData?.sshPublicKeys ?? [],
    });
    setSaveError(null);
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setSaveError(null);
  };

  // Uses the shared slug rule that the API re-exports, so the previewed path
  // matches the group the server will actually create.
  const generateGroupPath = (partyId: string): string => {
    const client = clients.find((c) => c.id === partyId);
    return client ? generateSftpGroupPath(client.name) : '';
  };

  const updateFormField = <K extends keyof EditFormState>(key: K, value: EditFormState[K]) => {
    setEditForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'isSftp' && value === true && !prev.keycloakGroup && prev.partyId) {
        next.keycloakGroup = generateGroupPath(prev.partyId);
      }
      if (key === 'partyId' && prev.isSftp) {
        next.keycloakGroup = generateGroupPath(value as string);
      }
      return next;
    });
  };

  // The access level is edited as two orthogonal controls — scope (internal vs external)
  // and tier (basic vs admin) — kept in sync with editForm.roles via the shared mapping.
  const editLevel = resolveAccessLevel(editForm.roles);
  const editIsInternalScope =
    editLevel === AccessLevel.INTERNAL_ADMIN || editLevel === AccessLevel.INTERNAL_BASIC;
  const editTier: 'basic' | 'admin' =
    editLevel === AccessLevel.INTERNAL_ADMIN || editLevel === AccessLevel.EXTERNAL_ADMIN
      ? 'admin'
      : 'basic';

  const setAccess = (next: { isInternal?: boolean; isAdmin?: boolean }) => {
    setEditForm((prev) => {
      const level = resolveAccessLevel(prev.roles);
      const isInternal =
        next.isInternal ??
        (level === AccessLevel.INTERNAL_ADMIN || level === AccessLevel.INTERNAL_BASIC);
      const isAdmin =
        next.isAdmin ??
        (level === AccessLevel.INTERNAL_ADMIN || level === AccessLevel.EXTERNAL_ADMIN);
      return {
        ...prev,
        roles: rolesForAccessLevel(accessLevelFromScopeTier(isInternal, isAdmin)) as RealmRole[],
      };
    });
  };

  const handleSave = async () => {
    if (!user?.id) return;
    setSaveLoading(true);
    setSaveError(null);

    try {
      if (!editForm.firstName.trim()) throw new Error('First name is required');
      if (!editForm.lastName.trim()) throw new Error('Last name is required');
      if (!editForm.email.trim()) throw new Error('Email is required');
      const phoneError = getPhoneNumberError(editForm.phoneNumber, { required: false });
      if (phoneError) throw new Error(phoneError);
      if (!editIsInternalScope && !editForm.partyId) throw new Error('Please select a client');

      if (editForm.isSftp) {
        // Same rules the API enforces, so the form fails fast.
        const sftpError = getSftpFieldsError({
          sftpUsername: editForm.sftpUsername,
          sshPublicKeys: editForm.sshPublicKeys,
        });
        if (sftpError) throw new Error(sftpError);
      }

      const payload: UpdateUserRequest = {};
      const data = detailedData || user;

      if (editForm.firstName !== (data.firstName || '')) payload.firstName = editForm.firstName;
      if (editForm.lastName !== (data.lastName || '')) payload.lastName = editForm.lastName;
      if (editForm.email !== (data.email || '')) payload.email = editForm.email;
      if (editForm.enabled !== data.enabled) payload.enabled = editForm.enabled;

      const originalPhone = detailedData?.phoneNumber || '';
      const normalizedPhone = editForm.phoneNumber
        ? normalizePhoneNumber(editForm.phoneNumber)
        : '';
      if (normalizedPhone !== originalPhone) {
        payload.phoneNumber = normalizedPhone || null;
      }

      // Internal users belong to the SteerFi internal party, not a specific client.
      const effectivePartyId = editIsInternalScope ? STEERFI_PARTY_ID : editForm.partyId;
      if (effectivePartyId !== (data.partyId || '')) payload.partyId = effectivePartyId;

      const originalLevel = resolveAccessLevel(data.roles ?? []);
      const editedLevel = resolveAccessLevel(editForm.roles);
      if (editedLevel !== originalLevel) {
        payload.roles = rolesForAccessLevel(editedLevel) as RealmRole[];
      }

      const originalIsSftp = detailedData?.isSftp || false;
      if (editForm.isSftp !== originalIsSftp) payload.isSftp = editForm.isSftp;

      if (editForm.isSftp) {
        const originalSftpUsername = detailedData?.sftpUsername || '';
        const currentSftpUsername = editForm.sftpUsername;
        if (currentSftpUsername !== originalSftpUsername || payload.isSftp === true) {
          payload.sftpUsername = currentSftpUsername || null;
        }

        // Only sent when the set actually changed. Disabling SFTP clears keys
        // server-side, so nothing needs to be sent in that case.
        const originalKeys = detailedData?.sshPublicKeys ?? [];
        const currentKeys = editForm.sshPublicKeys;
        const keysChanged =
          currentKeys.length !== originalKeys.length ||
          currentKeys.some((key, index) => key !== originalKeys[index]);
        if (keysChanged) {
          payload.sshPublicKeys = currentKeys;
        }
      }

      const originalGroup = detailedData?.keycloakGroup || '';
      if (editForm.keycloakGroup !== originalGroup) {
        payload.keycloakGroup = editForm.keycloakGroup || null;
      }

      // Internal users are never eligible for SFTP. Mirrors the API's condition:
      // only refuse when this save would touch SFTP or scope, so a user already in
      // that state (possible before the access-tier refactor) can still have
      // unrelated fields edited, and is fixed by unchecking Enable SFTP.
      if (
        editForm.isSftp &&
        editIsInternalScope &&
        (payload.isSftp !== undefined ||
          payload.roles !== undefined ||
          payload.sshPublicKeys !== undefined)
      ) {
        throw new Error(SFTP_NOT_FOR_INTERNAL_MESSAGE);
      }

      if (Object.keys(payload).length === 0) {
        setIsEditing(false);
        return;
      }

      await updateUser(user.id, payload);

      await fetchUserDetails(user.id);
      setIsEditing(false);
      onUserUpdated?.();
    } catch (err) {
      setSaveError(formatErrorMessage(err));
    } finally {
      setSaveLoading(false);
    }
  };

  const data = detailedData || user;
  const displayName =
    `${data?.firstName || ''} ${data?.lastName || ''}`.trim() || data?.username || 'Unknown';

  const formatTimestamp = (ts: number) => formatDate(ts);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[85vh]" onOpenAutoFocus={focusDialogOnOpen}>
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-primary" />
              {isEditing ? 'Edit User' : 'User Details'}
            </DialogTitle>
            {!isEditing && !loading && data && (
              <Button variant="outline" size="sm" onClick={enterEditMode} className="mr-6">
                <Pencil className="h-4 w-4 mr-1" />
                Edit
              </Button>
            )}
          </div>
          <DialogDescription>
            {isEditing
              ? 'Update user details. Changes to name and email will be synced to Keycloak.'
              : 'View detailed information about this user.'}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-12 text-destructive">
            <AlertCircle className="h-8 w-8 mb-2" />
            <p>{error}</p>
          </div>
        ) : data ? (
          isEditing ? (
            <div className="flex flex-col max-h-[calc(85vh-140px)] min-h-0">
              {saveError && (
                <div className="shrink-0 p-3 mb-3 bg-red-50 border border-red-200 rounded-md flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
                  <p className="text-sm text-red-700">{saveError}</p>
                </div>
              )}
              <div className="flex-1 min-h-0 overflow-y-auto">
                <div className="space-y-4 pr-2">
                  <div className="grid gap-4 py-2">
                    <FormRow label="First Name" htmlFor="edit-firstName" required>
                      <Input
                        id="edit-firstName"
                        value={editForm.firstName}
                        onChange={(e) => updateFormField('firstName', e.target.value)}
                        placeholder="First name"
                      />
                    </FormRow>

                    <FormRow label="Last Name" htmlFor="edit-lastName" required>
                      <Input
                        id="edit-lastName"
                        value={editForm.lastName}
                        onChange={(e) => updateFormField('lastName', e.target.value)}
                        placeholder="Last name"
                      />
                    </FormRow>

                    <FormRow label="Email" htmlFor="edit-email" required>
                      <Input
                        id="edit-email"
                        type="email"
                        value={editForm.email}
                        onChange={(e) => updateFormField('email', e.target.value)}
                        placeholder="user@example.com"
                      />
                    </FormRow>

                    <FormRow label="Phone" htmlFor="edit-phone">
                      <Input
                        id="edit-phone"
                        type="tel"
                        value={editForm.phoneNumber}
                        onChange={(e) => updateFormField('phoneNumber', e.target.value)}
                        placeholder="Optional"
                      />
                    </FormRow>

                    <FormRow label="Client" htmlFor="edit-partyId" required={!editIsInternalScope}>
                      <Select
                        value={editIsInternalScope ? '' : editForm.partyId}
                        onValueChange={(value) => updateFormField('partyId', value)}
                        disabled={editIsInternalScope}
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={
                              editIsInternalScope
                                ? 'SteerFi (internal user)'
                                : clientsLoading
                                  ? 'Loading clients...'
                                  : 'Select a client'
                            }
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {clients.map((client) => (
                            <SelectItem key={client.id} value={client.id}>
                              {client.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormRow>

                    <FormRow label="Status" alignTop>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="edit-enabled"
                          checked={editForm.enabled}
                          onCheckedChange={(checked) =>
                            updateFormField('enabled', checked === true)
                          }
                        />
                        <Label htmlFor="edit-enabled" className="font-normal cursor-pointer">
                          Account Enabled
                        </Label>
                      </div>
                    </FormRow>

                    <FormRow label="Scope" alignTop>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="edit-scope-internal"
                          checked={editIsInternalScope}
                          onCheckedChange={(checked) => setAccess({ isInternal: checked === true })}
                          disabled={saveLoading}
                        />
                        <Label htmlFor="edit-scope-internal" className="font-normal cursor-pointer">
                          <span className="font-medium">SteerFi internal user</span>
                          <span className="text-muted-foreground ml-1">
                            — cross-client internal access
                          </span>
                        </Label>
                      </div>
                    </FormRow>

                    <FormRow label="Role" htmlFor="edit-tier" required>
                      <Select
                        value={editTier}
                        onValueChange={(value) => setAccess({ isAdmin: value === 'admin' })}
                        disabled={saveLoading}
                      >
                        <SelectTrigger id="edit-tier">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="basic">Basic</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormRow>

                    <div className="border-t pt-4 mt-2">
                      <FormRow label="SFTP Access" alignTop>
                        <SftpAccessFields
                          idPrefix="edit"
                          isSftp={editForm.isSftp}
                          onIsSftpChange={(value) => updateFormField('isSftp', value)}
                          sftpUsername={editForm.sftpUsername}
                          onSftpUsernameChange={(value) => updateFormField('sftpUsername', value)}
                          sshPublicKeys={editForm.sshPublicKeys}
                          onSshPublicKeysChange={(keys) => updateFormField('sshPublicKeys', keys)}
                          email={editForm.email}
                          groupPath={editForm.keycloakGroup}
                          enableNotice="Enabling SFTP removes OTP/MFA credentials. This user will only be able to authenticate via SFTP. They will not be able to log into the web app without re-enrolling in MFA."
                          disabled={saveLoading}
                          ineligibleReason={
                            editIsInternalScope ? SFTP_NOT_FOR_INTERNAL_MESSAGE : null
                          }
                        />
                      </FormRow>
                    </div>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium flex items-center gap-2">
                          <Clock className="h-4 w-4" />
                          Timestamps
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Account Created</span>
                          <span>
                            {data.createdTimestamp
                              ? formatDateTime(data.createdTimestamp)
                              : '\u2014'}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Last Updated</span>
                          <span>
                            {detailedData?.updatedAt
                              ? formatDate(detailedData.updatedAt)
                              : '\u2014'}
                          </span>
                        </div>
                        {data.username && (
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Username</span>
                            <span className="font-mono text-xs">{data.username}</span>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </div>
              <div className="shrink-0 flex justify-end gap-2 pt-3 border-t mt-3">
                <Button variant="outline" size="sm" onClick={cancelEdit} disabled={saveLoading}>
                  <X className="h-4 w-4 mr-1" />
                  Cancel
                </Button>
                <Button size="sm" onClick={handleSave} disabled={saveLoading}>
                  {saveLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4 mr-1" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <div className="max-h-[calc(85vh-120px)] overflow-y-auto">
              <div className="space-y-4 pr-2">
                {/* Header Card */}
                <Card className="border-primary/20">
                  <CardContent className="p-4">
                    <div className="text-center mb-3">
                      <h2 className="text-xl font-semibold text-foreground">{displayName}</h2>
                      {data.username && data.username !== displayName && (
                        <p className="text-sm text-muted-foreground mt-1">@{data.username}</p>
                      )}
                    </div>
                    <div className="flex items-center justify-center gap-2">
                      <Badge variant={data.enabled ? 'default' : 'destructive'}>
                        {data.enabled ? 'Active' : 'Disabled'}
                      </Badge>
                      {data.emailVerified && <Badge variant="outline">Email Verified</Badge>}
                      {detailedData?.isSftp && <Badge variant="secondary">SFTP</Badge>}
                    </div>
                  </CardContent>
                </Card>

                {/* Tabs */}
                <Tabs defaultValue="overview" className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="overview">Overview</TabsTrigger>
                    <TabsTrigger value="access">Roles & Access</TabsTrigger>
                  </TabsList>

                  {/* Overview Tab */}
                  <TabsContent value="overview" className="mt-4">
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-3">
                        <InfoCard
                          icon={<Mail className="h-4 w-4" />}
                          title="Email"
                          value={data.email || '\u2014'}
                        />
                        <InfoCard
                          icon={<Phone className="h-4 w-4" />}
                          title="Phone"
                          value={detailedData?.phoneNumber || '\u2014'}
                        />
                        <InfoCard
                          icon={<Building2 className="h-4 w-4" />}
                          title="Client"
                          value={data.clientName || '\u2014'}
                        />
                        <InfoCard
                          icon={<Calendar className="h-4 w-4" />}
                          title="Created"
                          value={
                            data.createdTimestamp
                              ? formatTimestamp(data.createdTimestamp)
                              : '\u2014'
                          }
                        />
                      </div>

                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm font-medium flex items-center gap-2">
                            <Clock className="h-4 w-4" />
                            Timestamps
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Account Created</span>
                            <span>
                              {data.createdTimestamp
                                ? formatDateTime(data.createdTimestamp)
                                : '\u2014'}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Last Updated</span>
                            <span>
                              {detailedData?.updatedAt
                                ? formatDate(detailedData.updatedAt)
                                : '\u2014'}
                            </span>
                          </div>
                          {data.username && (
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">Username</span>
                              <span className="font-mono text-xs">{data.username}</span>
                            </div>
                          )}
                        </CardContent>
                      </Card>

                      {detailedData?.isSftp && (
                        <Card>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-medium flex items-center gap-2">
                              <Shield className="h-4 w-4" />
                              SFTP Configuration
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-2">
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">SFTP Enabled</span>
                              <Badge variant="default">Yes</Badge>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">SFTP Username</span>
                              <span className="font-mono text-xs">
                                {detailedData.sftpUsername || data.email || '\u2014'}
                              </span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">Keycloak Group</span>
                              <span className="font-mono text-xs">
                                {detailedData.keycloakGroup || '\u2014'}
                              </span>
                            </div>
                            <div className="pt-1 border-t">
                              <span className="text-sm text-muted-foreground">SSH Public Keys</span>
                              {detailedData.sshPublicKeys?.length ? (
                                <ul className="mt-1.5 space-y-1">
                                  {detailedData.sshPublicKeys.map((key) => (
                                    <li key={key} className="flex items-center gap-2 min-w-0">
                                      <SshPublicKeySummary sshKey={key} />
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <p className="text-xs text-muted-foreground italic mt-1">
                                  None \u2014 this user authenticates with a password.
                                </p>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground">
                              OTP/MFA is disabled for SFTP users
                            </p>
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  </TabsContent>

                  {/* Roles & Access Tab */}
                  <TabsContent value="access" className="mt-4">
                    <div className="grid grid-cols-2 gap-3">
                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm font-medium flex items-center gap-2">
                            <Shield className="h-4 w-4" />
                            Access Level
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <Badge variant="default" className="text-sm">
                            {ACCESS_LEVEL_LABEL[resolveAccessLevel(data.roles)]}
                          </Badge>
                        </CardContent>
                      </Card>

                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm font-medium flex items-center gap-2">
                            <User className="h-4 w-4" />
                            Account Status
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Status</span>
                            <Badge variant={data.enabled ? 'default' : 'destructive'}>
                              {data.enabled ? 'Active' : 'Disabled'}
                            </Badge>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Email Verified</span>
                            <Badge variant={data.emailVerified ? 'default' : 'outline'}>
                              {data.emailVerified ? 'Verified' : 'Not Verified'}
                            </Badge>
                          </div>
                          {data.clientName && (
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">Client Association</span>
                              <span>{data.clientName}</span>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </div>
                  </TabsContent>
                </Tabs>
              </div>
            </div>
          )
        ) : null}
      </DialogContent>
    </Dialog>
  );
};
